import { useParams, Navigate, Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Check, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import BookingButton from "@/components/BookingButton";
import CTASection from "@/components/CTASection";
import { findIndustry, INDUSTRIES, CASE_STUDIES } from "@/data/content";

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
};

const USE_CASES = {
  manufacturing:  ["MES database platforms", "IoT telemetry pipelines", "Predictive maintenance data", "Global OEE reporting"],
  logistics:      ["Real-time shipment telemetry", "Warehouse management", "Route optimisation", "Operational reporting"],
  "supply-chain": ["Planning platforms", "Master data management", "Cross-region distribution", "Analytics and reporting"],
  retail:         ["Omnichannel inventory", "Customer 360 platforms", "Loyalty programs", "Demand forecasting"],
  technology:     ["Product database platforms", "High-throughput services", "Multi-region architectures", "Operational scale"],
  healthcare:     ["EHR / clinical data", "Claims processing", "Imaging pipelines", "Compliance-aligned analytics"],
  "financial-services": ["Payments platforms", "Trading data", "Risk and reporting", "Regulatory audit workloads"],
  saas:           ["Multi-tenant databases", "Per-tenant SLAs", "Schema evolution", "Metering and billing data"],
};

export default function IndustryDetail() {
  const { slug } = useParams();
  const ind = findIndustry(slug);
  if (!ind) return <Navigate to="/industries" replace />;
  const uses = USE_CASES[slug] || [];
  const related = CASE_STUDIES.filter((c) => c.industry.toLowerCase().includes(ind.name.split(" ")[0].toLowerCase())).slice(0, 3);

  return (
    <>
      <SEO title={ind.name} description={`Database and data engineering consulting for ${ind.name}. ${ind.summary}`} />

      <section className="bg-[var(--dm-navy)] text-white">
        <div className="dm-container py-24 lg:py-32 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="dm-overline text-[var(--dm-emerald)]">Industry</div>
            <h1 className="font-serif text-5xl sm:text-6xl mt-6 leading-[1.05]">{ind.name}</h1>
            <p className="text-white/75 text-lg mt-6 max-w-2xl leading-relaxed">{ind.summary}</p>
            <div className="mt-10 flex flex-wrap gap-3">
              <BookingButton label="Request Technical Assessment" meetingType="consultation" variant="primary" testId={`ind-${slug}-assessment`} />
              <BookingButton label="Schedule Discovery Call" meetingType="discovery" variant="ghost" testId={`ind-${slug}-discovery`} />
            </div>
          </div>
          <div className="lg:col-span-4 hidden lg:flex items-end">
            <div className="border border-white/15 p-6 bg-white/[0.04] w-full">
              <Icon name={ind.icon} className="w-8 h-8 text-[var(--dm-emerald)]" />
              <div className="font-serif text-xl mt-4">Common use cases</div>
              <div className="text-xs text-white/60 mt-2">{uses.length} typical workloads</div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="dm-container dm-section grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7">
            <div className="dm-overline">Where we help</div>
            <h2 className="font-serif text-4xl text-[var(--dm-navy)] mt-4">Typical {ind.name.toLowerCase()} data workloads.</h2>
            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {uses.map((u) => (
                <li key={u} className="flex gap-3 text-[var(--dm-navy)] text-sm">
                  <Check className="w-4 h-4 mt-1 text-[var(--dm-emerald)]" /> {u}
                </li>
              ))}
            </ul>
            <p className="text-[var(--dm-muted)] mt-10 leading-relaxed">Every engagement is scoped around the operational reality of {ind.name.toLowerCase()} — compliance, latency, seasonality and business rhythm. Our engineering approach adapts accordingly.</p>
          </div>
          <aside className="lg:col-span-5">
            <div className="dm-overline">Other industries</div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {INDUSTRIES.filter((i) => i.slug !== slug).slice(0, 8).map((i) => (
                <Link key={i.slug} to={`/industries/${i.slug}`} className="text-sm text-[var(--dm-navy)] hover:text-[var(--dm-emerald)] border-b border-[var(--dm-border)] py-2">{i.name}</Link>
              ))}
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-[var(--dm-surface)]">
          <div className="dm-container dm-section">
            <div className="dm-overline">Related engagement examples</div>
            <h2 className="font-serif text-4xl text-[var(--dm-navy)] mt-4">Anonymised engagements in {ind.name}.</h2>
            <div className="grid md:grid-cols-3 gap-6 mt-10">
              {related.map((c) => (
                <Link key={c.slug} to={`/case-studies/${c.slug}`} className="group bg-white border border-[var(--dm-border)] hover:border-[var(--dm-emerald)]">
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={c.image} alt={c.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6">
                    <div className="dm-overline">{c.industry}</div>
                    <div className="font-serif text-lg text-[var(--dm-navy)] mt-3">{c.title}</div>
                    <div className="text-xs text-[var(--dm-muted)] mt-3 leading-relaxed">{c.summary}</div>
                    <div className="mt-4 text-xs inline-flex items-center gap-1 text-[var(--dm-navy)]">Read <ArrowRight className="w-3 h-3" /></div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </>
  );
}
